import Loading from "@/components/Loading";
import useCheckout from "@/lib/queries/payment/useCheckout";
import useUnauthenticatedCheckout from "@/lib/queries/payment/useUnauthenticatedCheckout";
import useUser from "@/lib/queries/user/useUser";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { loadStripe, StripeEmbeddedCheckoutOptions } from "@stripe/stripe-js";
import { useStytchSession } from "@stytch/react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY, {});

function Billing() {
  const [queryParams, _] = useSearchParams();
  const isYearlyParam = queryParams.get("yearly");
  const promotionCodeParam = queryParams.get("promo");
  const [isYearly, setIsYearly] = useState(isYearlyParam === "true");
  const [promotionCode, setPromotionCode] = useState(
    promotionCodeParam?.toUpperCase() || ""
  );
  const [appliedPromotionCode, setAppliedPromotionCode] = useState(
    promotionCodeParam?.toUpperCase() || ""
  );
  const returnPath = `/checkout/return`;
  const encodedReturnPath = encodeURI(returnPath);

  const handleApplyPromoCode = () => {
    if (promotionCode.trim() && promotionCode.toUpperCase() !== appliedPromotionCode) {
      setAppliedPromotionCode(promotionCode.trim().toUpperCase());
    }
  };

  const handlePromotionCodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApplyPromoCode();
    }
  };

  const stytchSession = useStytchSession();
  const isAuthenticated = stytchSession.session !== null;

  // Use different checkout hooks based on authentication status
  const authenticatedCheckout = useCheckout(encodedReturnPath, {
    yearly: isYearly,
    promotionCode: appliedPromotionCode || undefined,
    enabled: isAuthenticated,
  });

  const unauthenticatedCheckout = useUnauthenticatedCheckout(
    encodedReturnPath,
    {
      yearly: isYearly,
      promotionCode: appliedPromotionCode || undefined,
      enabled: !isAuthenticated,
    }
  );

  const { promise: fetchClientSecret } = isAuthenticated
    ? authenticatedCheckout
    : unauthenticatedCheckout;

  const options = {
    fetchClientSecret: () => fetchClientSecret,
  } as StripeEmbeddedCheckoutOptions;

  const user = useUser({
    enabled: isAuthenticated,
  });

  if (isAuthenticated && user.isLoading) {
    return <Loading />;
  }

  // For authenticated users who are already subscribed, show subscription info
  if (isAuthenticated && user.data?.subscribed) {
    return (
      <div className="flex flex-col flex-grow gap-6 px-6 pt-6 overflow-hidden overflow-y-scroll bg-card">
        <div className="flex flex-col max-w-screen-lg gap-4 mx-4">
          <h2 className="text-2xl font-semibold">You're already subscribed!</h2>
          <p>You already have an active subscription to Teacher Tools.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow gap-6 px-6 pt-6 overflow-hidden overflow-y-scroll bg-white">
      <div className="flex flex-col max-w-screen-lg gap-4 mx-4">
        <div className="flex flex-col items-start justify-between w-full gap-4 mb-4 sm:flex-row sm:items-center">
          {/* Monthly/Yearly Toggle */}
          <div className="flex items-center gap-1 p-1 border rounded-lg bg-muted/50">
            <Button
              variant={!isYearly ? "default" : "ghost"}
              size="sm"
              onClick={() => setIsYearly(false)}
            >
              Monthly
            </Button>
            <Button
              variant={isYearly ? "default" : "ghost"}
              size="sm"
              onClick={() => setIsYearly(true)}
              className="relative"
            >
              Yearly
              {isYearly && (
                <Badge
                  variant="secondary"
                  className="absolute -top-3 -right-3 text-xs px-1.5 py-0.5 pointer-events-none shadow-[0_0_0_1px_rgba(0,0,0,0.1)]"
                >
                  Best value!
                </Badge>
              )}
            </Button>
          </div>

          {/* Promo Code Input */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              Promo Code:
            </span>
            <Input
              type="text"
              placeholder="Enter code"
              value={promotionCode}
              onChange={(e) => setPromotionCode(e.target.value)}
              onKeyDown={handlePromotionCodeKeyDown}
              className="w-32 uppercase"
            />
            {appliedPromotionCode && promotionCode.toUpperCase() === appliedPromotionCode ? (
              <Button
                onClick={() => {
                  setAppliedPromotionCode("");
                  setPromotionCode("");
                }}
                variant="ghost"
                size="sm"
              >
                Remove
              </Button>
            ) : (
              <Button
                onClick={handleApplyPromoCode}
                disabled={!promotionCode.trim()}
                variant="outline"
                size="sm"
              >
                Apply
              </Button>
            )}
          </div>
        </div>

        {appliedPromotionCode && (
          <div className="p-3 mb-4 border border-green-200 rounded-lg bg-green-50">
            <p className="text-sm text-green-800">
              🎉 Promotion code <strong>{appliedPromotionCode}</strong> will be
              applied at checkout
            </p>
          </div>
        )}
        <EmbeddedCheckoutProvider
          key={`${isYearly ? "yearly" : "monthly"}-${isAuthenticated ? "auth" : "guest"}-${appliedPromotionCode || "no-promo"}`}
          stripe={stripePromise}
          options={options}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  );
}

export default Billing;
